import React from 'react';
import moment from 'moment';

import {
  ChevronRight,
  NotificationsNone,
} from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  Chip,
  Typography,
  useColorScheme,
} from '@mui/material';
import {
  alpha,
  darken,
  lighten,
  useTheme,
} from '@mui/material/styles';

import { getReminderStatusOption } from '../../../../../../pages/settings/TaskPage/reminders/components/TagReminder/statusChip';

const PlannerReminderCard = ({
  suggestion,
  onClick,
}) => {
  const theme = useTheme();
  const {
    mode,
    systemMode,
  } = useColorScheme();
  const darkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark') || (!mode && theme.palette.mode === 'dark');
  const template = suggestion.template || suggestion.reminder?.eventTemplateReminder?.eventTemplate;
  const taskColor = template?.eventDataDefaultValues?.calendarDisplayColor || theme.palette.primary.main;
  const tint = darkMode
    ? lighten(taskColor, 0.5)
    : darken(taskColor, 0.2);
  const opacity = darkMode
    ? 0.1
    : 0.05;
  const captionColor = darkMode
    ? 'text.primary'
    : 'text.secondary';
  const status = getReminderStatusOption(suggestion.reminder?.status);
  const statusPalette = theme.palette[status.color] || theme.palette.grey;
  const statusMain = statusPalette.main || statusPalette[600];
  const statusColor = darkMode
    ? lighten(statusMain, 0.65)
    : statusPalette.dark || statusPalette[800];
  const statusBackground = darkMode
    ? darken(statusMain, 0.8)
    : lighten(statusMain, 0.92);
  const taskName = template?.name || 'Task';
  const horseName = suggestion.barnHorse?.horse?.name || suggestion.reminder?.tag?.name || 'horse';
  const taskDate = suggestion.task?.startDate;
  let originalDateLabel = '';
  if (suggestion.action === 'complete' && taskDate && moment(taskDate).isValid() && moment(taskDate).isBefore(suggestion.date, 'day')) {
    originalDateLabel = `From ${moment(taskDate).format('MMM D')}`;
  }

  return (
    <ButtonBase
      component="button"
      type="button"
      aria-label={`${taskName} for ${horseName}: Reminder · ${status.label}. View reminder`}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
      }}
      onClick={(event) => {
        event.stopPropagation();
        onClick(suggestion);
      }}
      sx={{
        width: '100%',
        minWidth: 0,
        minHeight: 44,
        gap: 0.75,
        p: 0.75,
        textAlign: 'left',
        justifyContent: 'flex-start',
        border: `1px dashed ${alpha(tint, 0.45)}`,
        borderLeft: `4px solid ${taskColor}`,
        borderRadius: 1,
        backgroundColor: alpha(taskColor, opacity),
        color: 'text.primary',
        '&:hover': { backgroundColor: alpha(tint, 0.15) },
        '&.Mui-focusVisible': {
          outline: '2px solid',
          outlineColor: 'text.primary',
          outlineOffset: 2,
          backgroundColor: alpha(tint, 0.15),
        },
      }}
    >
      <NotificationsNone sx={{ color: tint }} fontSize="small" />
      <Box
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
        }}
      >
        <Typography
          component="span"
          sx={{
            display: 'block',
            fontSize: '0.85rem',
            fontWeight: 600,
            overflowWrap: 'anywhere',
          }}
        >{taskName}
        </Typography>
        <Box
          component="span"
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.5,
            mt: 0.25,
          }}
        >
          <Typography component="span" variant="caption" color={captionColor}>Reminder</Typography>
          <Chip
            component="span"
            label={status.label}
            color={status.color}
            size="small"
            sx={{
              height: 'auto',
              minHeight: 20,
              maxHeight: 'none',
              maxWidth: '100%',
              fontSize: '0.65rem',
              '&&': {
                color: statusColor,
                backgroundColor: statusBackground,
                '& .MuiChip-label': { color: 'inherit' },
              },
              '& .MuiChip-label': {
                px: 0.75,
                py: 0.125,
                whiteSpace: 'normal',
              },
            }}
          />
        </Box>
        {originalDateLabel && (
          <Typography component="span" variant="caption" color={captionColor}>{originalDateLabel}</Typography>
        )}
      </Box>
      <ChevronRight fontSize="small" sx={{ color: 'text.secondary' }} />
    </ButtonBase>
  );
};

export default PlannerReminderCard;
